import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Information } from "@/components/Information/Information";
import { SvgIcon } from "@/components/Icon/SvgIcon";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";
import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { InclusionFilterStatus } from "@/enum/list.enum";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { List } from "../../../../schema/list";
import { buildShowLabel, getGroupedShowOptions } from "../../utils/getGroupedShowOptions";
import {
	getSelectedReasons,
	GroupedShowSelection,
	isBucketFullySelected,
	toggleBucket,
	toggleReason,
} from "../../utils/groupedShowSelection";

export interface GroupedShowFilterProps {
	ts: (key: string) => string;
	list?: List;
	statuses: InclusionFilterStatus[];
	exclusionReasons: string[];
	onChange: (statuses: InclusionFilterStatus[], exclusionReasons: string[]) => void;
	/** Extension reuse escape hatch (web rule §11): portal target defaults to document.body. */
	portalContainer?: HTMLElement | null;
}

/**
 * The grouped "Show" filter (PREC-3536 Phase 5): the four inclusion buckets with expandable
 * per-reason sub-rows + counts, replacing the flat `DropdownWithCheckboxes`. Mirrors the
 * prototype's `dd-menu` and the shared checkbox-dropdown styling; menu rendered via
 * `createPortal` (web rule §11).
 */
export const GroupedShowFilter: FunctionComponent<GroupedShowFilterProps> = ({
	ts,
	list,
	statuses,
	exclusionReasons,
	onChange,
	portalContainer,
}) => {
	const [open, setOpen] = useState(false);
	const [expanded, setExpanded] = useState<InclusionFilterStatus[]>([]);
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, open);
	const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
		null
	);

	const selection = useMemo<GroupedShowSelection>(
		() => ({ statuses, exclusionReasons }),
		[statuses, exclusionReasons]
	);
	const buckets = useMemo(() => getGroupedShowOptions(ts, list), [ts, list]);
	const label = useMemo(() => buildShowLabel(ts, selection, buckets), [ts, selection, buckets]);

	// Default-expand buckets that have sub-reasons — once, when the reason counts first load
	// (they come from async list data). After that the user's collapse/expand choices win.
	const didInitExpandRef = useRef(false);

	useEffect(() => {
		if (didInitExpandRef.current) {
			return;
		}

		const withReasons = buckets
			.filter((bucket) => bucket.reasons.length > 0)
			.map((bucket) => bucket.status);

		if (withReasons.length > 0) {
			setExpanded(withReasons);
			didInitExpandRef.current = true;
		}
	}, [buckets]);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);
			const insideTrigger = triggerRef.current && triggerRef.current.contains(target);
			const insideMenu = menuRef.current && menuRef.current.contains(target);

			if (!insideTrigger && !insideMenu && open) {
				setOpen(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const updatePosition = () => {
			if (triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();

				setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
			}
		};

		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	const emitChange = (next: GroupedShowSelection) => {
		if (next !== selection) {
			onChange(next.statuses, next.exclusionReasons);
		}
	};

	const toggleExpanded = (status: InclusionFilterStatus) =>
		setExpanded((current) =>
			current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
		);

	const renderBucket = (bucket: (typeof buckets)[number]) => {
		const knownParams = bucket.reasons.map((reason) => reason.filterParam);
		const selectedParams = getSelectedReasons(selection, bucket.status, knownParams);
		const isExpanded = expanded.includes(bucket.status);

		return (
			<div key={bucket.status}>
				<div
					className="flex flex-row items-center justify-between gap-1 text-sm text-gray-900 bg-white hover:bg-gray-100 duration-200 cursor-pointer whitespace-nowrap pr-2"
					data-test={`show-bucket-${bucket.status}`}
				>
					<Checkbox
						className="pl-2"
						isChecked={isBucketFullySelected(selection, bucket.status)}
						labelClass="cursor-pointer py-2.5 px-3 pl-0"
						onChange={() => emitChange(toggleBucket(selection, bucket.status))}
					>
						<div className="flex flex-row items-center gap-1">
							<span>{bucket.title}</span>
							{bucket.information && <Information information={bucket.information} />}
							<span className="text-brand-light">{bucket.count || ""}</span>
						</div>
					</Checkbox>
					{bucket.reasons.length > 0 && (
						<button
							className="cursor-pointer p-1 text-gray-500 hover:text-gray-900"
							data-test={`show-expand-${bucket.status}`}
							onClick={() => toggleExpanded(bucket.status)}
						>
							<SvgIcon
								className="w-[16px] h-[16px] pointer-events-none"
								svgIcon={isExpanded ? ChevronUp : ChevronDown}
							/>
						</button>
					)}
				</div>
				{isExpanded &&
					bucket.reasons.map((reason) => (
						<div
							key={`${bucket.status}-${reason.filterParam}`}
							className="flex flex-row items-center text-sm text-gray-900 bg-white hover:bg-gray-100 duration-200 cursor-pointer whitespace-nowrap pl-6 pr-2"
							data-test={`show-reason-${bucket.status}-${reason.filterParam}`}
						>
							<Checkbox
								className="pl-2"
								isChecked={selectedParams.includes(reason.filterParam)}
								labelClass="cursor-pointer py-2 px-3 pl-0"
								onChange={() =>
									emitChange(
										toggleReason(selection, bucket.status, reason.filterParam, knownParams)
									)
								}
							>
								<div className="flex flex-row items-center gap-2">
									<span className="text-text-meta">{reason.label}</span>
									<span className="text-brand-light">{reason.count || ""}</span>
								</div>
							</Checkbox>
						</div>
					))}
			</div>
		);
	};

	return (
		<div ref={triggerRef} className="relative w-full">
			<button
				className={classNames(
					"input-styles relative inline-flex w-full pl-3 pr-8 shadow-sm cursor-pointer",
					"py-1 !h-[32px] !rounded-[10px] text-xs font-semibold"
				)}
				data-test="grouped-show-trigger"
				onClick={() => setOpen(!open)}
			>
				<div className="inline-flex w-full items-center min-w-0">
					<div className="flex-shrink-0">{ts("showFilter.show")}</div>
					<div className="flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis text-brand-light">
						{label ? `: ${label}` : ""}
					</div>
				</div>
				<div className="absolute right-[10px] top-[50%] translate-y-[-50%]">
					<SvgIcon
						className="w-[20px] h-[20px] pointer-events-none"
						svgIcon={open ? ChevronUp : ChevronDown}
					/>
				</div>
			</button>

			{open &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="fixed w-max z-[2000] mt-1 bg-white border border-gray-200 rounded-md max-h-[320px] overflow-y-auto bb-scrollbar-darker"
						data-test="grouped-show-menu"
						style={
							position
								? { top: position.top, left: position.left, minWidth: position.width }
								: { visibility: "hidden" }
						}
						onClick={(e) => e.stopPropagation()}
					>
						{buckets.map(renderBucket)}
					</div>,
					portalContainer ?? document.body
				)}
		</div>
	);
};
